// Daily work — what each salesperson actually did in the chosen days, and what
// they have left undone right now (client, 2026-10-28). Every number opens the
// list of the records behind it. Server: src/shared/sales-performance/work-monitor.js.
import { useUrlState } from '@/shared/hooks/useUrlState';
import { Link } from 'react-router-dom';
import { AlarmClock, CalendarCheck, CalendarClock, ClipboardList, FileText, PackageX, PhoneOff, UserPlus } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { inr, useWorkDays, useWorkList, useWorkMonitor, type WorkItem, type WorkMetric, type WorkPeriod, type WorkPreset } from '../api';
import { C, Dialog, Kpi, Panel } from './ui';

const PRESETS: Array<{ value: WorkPreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'custom', label: 'Custom' },
];

/** Columns: period activity first, then today's backlog (red when not zero). */
const PERIOD_COLS: Array<{ key: WorkMetric; label: string; problem?: boolean }> = [
  { key: 'newLeads', label: 'New leads' },
  { key: 'followupsAdded', label: 'Follow-ups added' },
  { key: 'followupsDone', label: 'Follow-ups done' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'proformas', label: 'Proformas' },
];
const NOW_COLS: Array<{ key: WorkMetric; label: string; problem?: boolean }> = [
  { key: 'dueTodayFollowups', label: 'Follow-ups due today' },
  { key: 'overdueFollowups', label: 'Follow-ups overdue', problem: true },
  { key: 'quotationsNoFollowup', label: 'Open quotation, no follow-up', problem: true },
  { key: 'proformasNoFollowup', label: 'Open proforma, no follow-up', problem: true },
  { key: 'untouchedLeads', label: 'New, not completed', problem: true },
  { key: 'noNextFollowup', label: 'No next follow-up', problem: true },
  { key: 'noProduct', label: 'No product', problem: true },
  { key: 'noContact', label: 'No contact details', problem: true },
];
/** Leads held — Open (active) vs Won / Lost (inactive) — as on the same date. */
const HELD_COLS: Array<{ key: WorkMetric; label: string; problem?: boolean }> = [
  { key: 'openRecords', label: 'Open (active)' },
  { key: 'wonRecords', label: 'Won' },
  { key: 'lostRecords', label: 'Lost' },
];
/** Day-by-day columns. */
const DAY_COLS: Array<{ key: keyof import('../api').WorkDay; label: string; problem?: boolean }> = [
  { key: 'newLeads', label: 'New leads' },
  { key: 'followupsAdded', label: 'Follow-ups added' },
  { key: 'followupsDone', label: 'Follow-ups done' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'proformas', label: 'Proformas' },
  { key: 'dueTodayFollowups', label: 'Due that day' },
  { key: 'overdueFollowups', label: 'Overdue at day end', problem: true },
  { key: 'untouchedLeads', label: 'New, not completed', problem: true },
  { key: 'quotationsNoFollowup', label: 'Quotation, no follow-up', problem: true },
];

const today = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

function linkOf(item: WorkItem): string | null {
  if (item.link !== undefined) return item.link;
  if (item.kind === 'quotation') return `/quotations/${item.id}`;
  if (item.kind === 'proforma') return `/proforma/${item.id}`;
  if (item.kind === 'followup') {
    if (item.parentType === 'quotation') return `/quotations/${item.parentId}`;
    if (item.parentType === 'proforma') return `/proforma/${item.parentId}`;
    return `/pipeline/${item.parentId}`;
  }
  return `/pipeline/${item.id}`;
}

export function WorkMonitorTab() {
  // Kept in the URL so Back from a record reopens the same period and list (2026-10-30).
  const [period, setPeriod] = useUrlState<WorkPeriod>('period', { preset: 'today' });
  const [open, setOpen] = useUrlState<{ metric: WorkMetric; employee: string | null; name: string } | null>('list', null);
  const [dayEmployee, setDayEmployee] = useUrlState<string>('dayOf', '');
  const { data, isLoading, error } = useWorkMonitor(period);
  const days = useWorkDays(period, dayEmployee || null);
  // "Right now" only when the period runs into today; otherwise "As on <date>".
  const asOfLabel = data && !data.period.asOfIsNow && data.period.asOf
    ? `As on ${fmtDate(data.period.asOf)} (end of day)` : 'Right now';
  const multiDay = data ? new Date(data.period.end).getTime() - new Date(data.period.start).getTime() > 24 * 3600 * 1000 : false;
  const list = useWorkList(period, open?.metric ?? null, open?.employee ?? null);
  const t = data?.totals;
  const show = (metric: WorkMetric, employee: string | null, name: string) => setOpen({ metric, employee, name });
  const rows = [...(data?.rows ?? [])].sort((a, b) =>
    (b.overdueFollowups + b.untouchedLeads + b.noNextFollowup) - (a.overdueFollowups + a.untouchedLeads + a.noNextFollowup));

  const cell = (key: string, n: number, problem: boolean | undefined, onClick: () => void) => (
    <td key={key} className="px-3 py-2.5 text-right tabular-nums">
      {n > 0 ? (
        <button type="button" onClick={onClick}
          className={`rounded-md px-2 py-0.5 font-semibold hover:underline ${problem ? 'bg-rose-50 text-rose-700' : 'text-slate-800'}`}>{n}</button>
      ) : <span className="text-slate-300">0</span>}
    </td>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200/80 bg-white p-3">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {PRESETS.map((p) => (
            <button key={p.value} type="button"
              onClick={() => setPeriod((cur) => (p.value === 'custom' ? { preset: 'custom', from: cur.from || today(), to: cur.to || today() } : { preset: p.value }))}
              className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition ${period.preset === p.value ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
          ))}
        </div>
        {period.preset === 'custom' && (
          <div className="flex items-end gap-2 text-sm">
            <label className="flex flex-col gap-1"><span className="text-data-xs font-semibold uppercase text-slate-500">From</span>
              <input type="date" value={period.from || ''} max={period.to || undefined} onChange={(e) => setPeriod((c) => ({ ...c, from: e.target.value }))} className="rounded-lg border border-slate-200 px-2 py-1.5" /></label>
            <label className="flex flex-col gap-1"><span className="text-data-xs font-semibold uppercase text-slate-500">To</span>
              <input type="date" value={period.to || ''} min={period.from || undefined} onChange={(e) => setPeriod((c) => ({ ...c, to: e.target.value }))} className="rounded-lg border border-slate-200 px-2 py-1.5" /></label>
          </div>
        )}
        {data && (
          <p className="ml-auto text-xs text-slate-500">
            Period: {fmtDate(data.period.start)}{data.period.preset !== 'today' && data.period.preset !== 'yesterday' ? ` – ${fmtDate(new Date(new Date(data.period.end).getTime() - 1).toISOString())}` : ''}
            {' · '}{data.period.asOfIsNow ? 'Backlog figures are as of right now.' : `Backlog figures are as they stood at the end of ${fmtDate(data.period.asOf)}.`}
          </p>
        )}
      </div>

      {isLoading && <div className="flex justify-center py-16"><Spinner /></div>}
      {error && <p className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700">Could not load the work monitor.</p>}

      {t && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <button type="button" className="text-left" onClick={() => show('dueTodayFollowups', null, 'Everyone')}><Kpi label="Follow-ups due today" value={t.dueTodayFollowups} icon={CalendarCheck} accent={C.blue} sub={data?.period.asOfIsNow ? 'to do today' : `on ${fmtDate(data?.period.asOf)}`} /></button>
          <button type="button" className="text-left" onClick={() => show('overdueFollowups', null, 'Everyone')}><Kpi label="Follow-ups overdue" value={t.overdueFollowups} icon={AlarmClock} accent={C.red} sub={data?.period.asOfIsNow ? 'due before today' : `as on ${fmtDate(data?.period.asOf)}`} /></button>
          <button type="button" className="text-left" onClick={() => show('untouchedLeads', null, 'Everyone')}><Kpi label="New leads not completed" value={t.untouchedLeads} icon={PhoneOff} accent={C.red} sub={t.oldestUntouchedDays ? `oldest ${t.oldestUntouchedDays} days` : asOfLabel.toLowerCase()} /></button>
          <button type="button" className="text-left" onClick={() => show('noNextFollowup', null, 'Everyone')}><Kpi label="No next follow-up" value={t.noNextFollowup} icon={CalendarClock} accent={C.amber} sub="open leads & deals" /></button>
          <button type="button" className="text-left" onClick={() => show('noProduct', null, 'Everyone')}><Kpi label="No product" value={t.noProduct} icon={PackageX} accent={C.amber} sub="open leads & deals" /></button>
          <button type="button" className="text-left" onClick={() => show('newLeads', null, 'Everyone')}><Kpi label="New leads" value={t.newLeads} icon={UserPlus} accent={C.blue} sub="in this period" /></button>
          <button type="button" className="text-left" onClick={() => show('quotationsNoFollowup', null, 'Everyone')}><Kpi label="Open quotation, no follow-up" value={t.quotationsNoFollowup} icon={FileText} accent={C.red} sub={asOfLabel.toLowerCase()} /></button>
        </div>
      )}

      {data && (
        <Panel title="Each salesperson" subtitle="Click any number to see the leads or documents behind it." icon={ClipboardList} pad={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-data-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2" />
                  <th colSpan={PERIOD_COLS.length} className="border-l border-slate-100 px-3 py-2 text-center font-semibold text-blue-700">In this period</th>
                  <th colSpan={NOW_COLS.length + 1} className="border-l border-slate-100 px-3 py-2 text-center font-semibold text-rose-700">{asOfLabel}</th>
                  <th colSpan={HELD_COLS.length} className="border-l border-slate-100 px-3 py-2 text-center font-semibold text-slate-700">Leads &amp; deals held</th>
                </tr>
                <tr className="border-b border-slate-100 text-left text-data-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Salesperson</th>
                  {PERIOD_COLS.map((c) => <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>)}
                  {NOW_COLS.map((c) => <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>)}
                  <th className="px-3 py-2 text-right">Oldest uncontacted</th>
                  {HELD_COLS.map((c) => <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employeeId} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{r.name}</td>
                    {PERIOD_COLS.map((c) => cell(c.key, r[c.key], c.problem, () => show(c.key, r.employeeId, r.name)))}
                    {NOW_COLS.map((c) => cell(c.key, r[c.key], c.problem, () => show(c.key, r.employeeId, r.name)))}
                    <td className={`px-3 py-2.5 text-right tabular-nums ${r.oldestUntouchedDays > 2 ? 'font-semibold text-rose-700' : 'text-slate-500'}`}>
                      {r.untouchedLeads ? `${r.oldestUntouchedDays} days` : '—'}
                    </td>
                    {HELD_COLS.map((c) => cell(c.key, r[c.key], c.problem, () => show(c.key, r.employeeId, r.name)))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {data && multiDay && (
        <Panel
          title="Day by day"
          subtitle="Each date of the period — what was done that day, and the backlog at the end of it. Click a date to open that day."
          icon={CalendarCheck}
          pad={false}
          right={(
            <select id="dw-day-employee" value={dayEmployee} onChange={(e) => setDayEmployee(e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">
              <option value="">Whole team</option>
              {(data.rows ?? []).map((r) => <option key={r.employeeId} value={r.employeeId}>{r.name}</option>)}
            </select>
          )}
        >
          {days.isLoading ? <div className="flex justify-center py-10"><Spinner /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-data-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Date</th>
                    {DAY_COLS.map((c) => <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(days.data?.days ?? []).map((d) => {
                    const ymd = new Date(new Date(d.date).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
                    return (
                      <tr key={d.date} className="border-b border-slate-50 hover:bg-slate-50/60">
                        <td className="px-3 py-2.5">
                          <button type="button" className="font-semibold text-blue-700 hover:underline"
                            onClick={() => setPeriod({ preset: 'custom', from: ymd, to: ymd })}>
                            {new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                          </button>
                        </td>
                        {DAY_COLS.map((c) => (
                          <td key={c.key} className={`px-3 py-2.5 text-right tabular-nums ${Number(d[c.key]) > 0 && c.problem ? 'font-semibold text-rose-700' : Number(d[c.key]) > 0 ? 'text-slate-800' : 'text-slate-300'}`}>
                            {d[c.key] as number}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} width="max-w-3xl"
        title={`${list.data?.label || (open ? data?.metrics[open.metric] : '') || ''} — ${open?.name || ''}`}
        subtitle={list.data ? `${list.data.total} record${list.data.total === 1 ? '' : 's'}${list.data.total > list.data.items.length ? ` · showing first ${list.data.items.length}` : ''}` : undefined}>
        {list.isLoading ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <ul className="divide-y divide-slate-100">
            {(list.data?.items ?? []).map((it) => (
              <li key={`${it.kind}:${it.id}`} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  {linkOf(it)
                    ? <Link to={linkOf(it) as string} className="font-semibold text-blue-700 hover:underline">{it.title}</Link>
                    : <span className="font-semibold text-slate-500">{it.title}</span>}
                  {it.sub && <p className="truncate text-xs text-slate-500">{it.sub}</p>}
                  {it.note && <p className="text-xs font-medium text-rose-700">{it.note}</p>}
                </div>
                <div className="shrink-0 text-right text-xs text-slate-500">
                  {open?.employee === null && it.employee && <p className="font-medium text-slate-700">{it.employee}</p>}
                  {it.date && <p>{fmtDate(it.date)}</p>}
                  {!!it.amount && <p>{inr(it.amount)}</p>}
                </div>
              </li>
            ))}
            {!list.data?.items.length && <li className="py-6 text-center text-sm text-slate-500">Nothing here.</li>}
          </ul>
        )}
      </Dialog>
    </div>
  );
}

